import { useCallback, useMemo, useState } from 'react';

import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { clog } from '@app/utils/clog';

import { getColumns } from '@pages/DublicateClients/data/columns';
import { initialFilters } from '@pages/DublicateClients/data/constants';
import {
  dublicateClientsMocks,
  dublicateStatusesMocks,
} from '@pages/DublicateClients/mocks';
import type {
  ClientModel,
  DublicateClientModel,
  DublicatesFilters,
} from '@pages/DublicateClients/types';

import type { PaginationProps } from 'antd';
import type { BaseOptionType } from 'antd/es/select';
import type { ChangeEventHandler } from 'react';

export const useDublicateClients = () => {
  const { showNotification } = useNotification();
  const [isOpenCombineClientsModal, setIsOpenCombineClientsModal] =
    useState(false);
  const [combineClients, setCombineClients] =
    useState<DublicateClientModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<DublicatesFilters>(
    () => initialFilters
  );
  const [unsavedFilters, setUnsavedFilters] = useState<DublicatesFilters>(
    () => initialFilters
  );
  const [data, setData] = useState<Array<DublicateClientModel>>([]);

  const [statusesOptions, setStatusesOptions] = useState<BaseOptionType[]>([]);

  const getFiltersHandler = useCallback(async () => {
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      setStatusesOptions(dublicateStatusesMocks);
    } catch (error) {
      showNotification(error);
    } finally {
      setIsLoading(false);
    }
  }, [showNotification]);

  const getDublicateClientsHandler = useCallback(async () => {
    setIsLoading(true);
    try {
      await new Promise((resolve) =>
        setTimeout(() => {
          clog('Filters:', filters);
          setData(dublicateClientsMocks);
          resolve(undefined);
        }, 500)
      );
    } catch (error) {
      showNotification(error);
    } finally {
      setIsLoading(false);
    }
  }, [showNotification, filters]);

  const onChangeSearchTerm: ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ target: { value: SearchTerm } }) => {
      setUnsavedFilters((prev) => ({
        ...prev,
        SearchTerm: SearchTerm || null,
      }));
    },
    []
  );

  const onChangeStatuses = useCallback((values: BaseOptionType[]) => {
    setUnsavedFilters((prev) => ({
      ...prev,
      statusesIds: values.map(({ value }) => Number(value)),
    }));
  }, []);

  const skipFilters = useCallback(() => {
    setUnsavedFilters(initialFilters);
    setFilters(initialFilters);
  }, []);

  const onSearch = useCallback(() => {
    setFilters(() => ({ ...unsavedFilters, PageN: 1 }));
  }, [unsavedFilters]);

  const onPaginate: PaginationProps['onChange'] = useCallback(
    (PageN: number) => {
      setFilters((prev) => ({ ...prev, PageN }));
      setUnsavedFilters((prev) => ({ ...prev, PageN }));
    },
    []
  );

  const onPageSizeChange = useCallback((ItemsPerPage: number) => {
    setFilters((p) => ({ ...p, ItemsPerPage, PageN: 1 }));
    setUnsavedFilters((p) => ({ ...p, ItemsPerPage, PageN: 1 }));
  }, []);

  const disabled = useMemo(() => isLoading, [isLoading]);

  const isFetching = useMemo(() => isLoading, [isLoading]);
  const isPageLoading = useMemo(() => isLoading, [isLoading]);

  const openCombineClientsModal = useCallback(
    (combineClients: DublicateClientModel) => {
      setIsOpenCombineClientsModal(true);
      setCombineClients(combineClients);
    },
    []
  );

  const closeCombineClientsModal = useCallback(() => {
    setIsOpenCombineClientsModal(false);
    setCombineClients(null);
  }, []);

  const columns = useMemo(
    () => getColumns({ openCombineClientsModal, statusesOptions }),
    [openCombineClientsModal, statusesOptions]
  );

  const onSaveCombineClients = useCallback(
    async (resultClient: ClientModel) => {
      setIsLoading(true);
      try {
        await new Promise((resolve) =>
          setTimeout(() => {
            resolve(undefined);
            clog(resultClient);
          }, 500)
        );
        closeCombineClientsModal();
        await getDublicateClientsHandler();
        showNotification('Клиенты успешно объединены', 'success');
      } catch (error) {
        showNotification(error);
      } finally {
        setIsLoading(false);
      }
    },
    [closeCombineClientsModal, getDublicateClientsHandler, showNotification]
  );

  const onMarkNotDuplicate = useCallback(
    async (clientId: number) => {
      setIsLoading(true);
      try {
        await new Promise((resolve) =>
          setTimeout(() => {
            resolve(undefined);
            clog(clientId);
          }, 500)
        );

        showNotification('Клиент помечен как не дубликат', 'success');
      } catch (error) {
        showNotification(error);
      } finally {
        setIsLoading(false);
      }
    },
    [showNotification]
  );

  return {
    onChangeSearchTerm,
    onChangeStatuses,
    unsavedFilters,
    skipFilters,
    onSearch,
    getFiltersHandler,
    statusesOptions,
    getDublicateClientsHandler,
    disabled,
    data,
    onPaginate,
    onPageSizeChange,
    isFetching,
    isPageLoading,
    closeCombineClientsModal,
    columns,
    isOpenCombineClientsModal,
    combineClients,
    onSaveCombineClients,
    onMarkNotDuplicate,
  };
};
